/**
 * AdminPage - Admin console with tabbed layout
 * Phase 11.5 - Support, Ops & Growth
 */
import React, { useEffect, useState, useCallback } from 'react'
import {
  IoRefresh,
  IoStatsChart,
  IoCheckmarkCircle,
  IoWarning,
  IoTrendingUp,
  IoTrendingDown,
  IoShieldCheckmark,
  IoServer,
  IoPeople,
  IoDocument,
  IoSettings,
  IoAnalytics,
  IoCog,
  IoRadio,
  IoPulse,
  IoPieChart,
} from 'react-icons/io5'
import { apiGetAuth } from '../api'
import { useOnlineUsers, useFeatureUsage, useActivityTrend } from '../hooks/admin'
import { SkeletonCLVStats } from './Skeleton'
import {
  AccordionSection,
  CompactCard,
  CompactCardGrid,
  SupportConsole,
  OpsConsole,
  GrowthConsole,
  UserManagement,
} from './admin'
import { DatabasePage } from './admin-v2/database/DatabasePage'

// Types for CLV data
interface CLVStats {
  total_predictions: number
  avg_clv: number | null
  median_clv: number | null
  positive_clv_pct: number | null
  by_market: Record<string, { count: number; avg_clv: number }>
  by_confidence: Record<string, { count: number; avg_clv: number }>
  interpretation: string | null
}

interface CLVPrediction {
  id: number
  fixture_id: number
  fixture_date: string | null
  market_type: string
  line: number | null
  outcome: string
  predicted_prob: number
  pinnacle_odds: number | null
  pinnacle_implied_prob: number | null
  clv: number | null
  clv_pct: number | null
  actual_outcome: boolean | null
  brier_score: number | null
}

interface CLVPredictionsResponse {
  total: number
  offset: number
  limit: number
  predictions: CLVPrediction[]
}

interface AuditLogEntry {
  id: number
  action_type: string
  resource_type: string
  resource_id: string | null
  admin_user_id: number | null
  admin_email: string | null
  ip_address: string | null
  changes: Record<string, unknown> | null
  status: string
  error_message: string | null
  timestamp: string
}

interface AuditLogsResponse {
  total: number
  offset: number
  limit: number
  logs: AuditLogEntry[]
}

interface DBStats {
  tables: Record<string, { row_count: number; size_bytes: number }>
  total_size: string
  oldest_odds_history: string | null
  newest_odds_history: string | null
}

type AdminTab = 'overview' | 'users' | 'support' | 'ops' | 'growth' | 'analytics' | 'database'

// Helper to format CLV percentage
function formatCLV(clv: number | null): string {
  if (clv === null) return '—'
  const pct = clv * 100
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

// Helper to get CLV color class
function getCLVClass(clv: number | null): string {
  if (clv === null) return ''
  if (clv >= 0.02) return 'clv-strong-positive'
  if (clv >= 0) return 'clv-positive'
  if (clv >= -0.02) return 'clv-neutral'
  return 'clv-negative'
}

// Helper to get interpretation badge
function getInterpretationBadge(interpretation: string | null) {
  if (!interpretation) return null

  let icon = <IoStatsChart size={14} />
  let className = 'interpretation-badge'

  if (interpretation.includes('Strong evidence')) {
    icon = <IoCheckmarkCircle size={14} />
    className += ' strong-positive'
  } else if (interpretation.includes('slight edge')) {
    icon = <IoTrendingUp size={14} />
    className += ' positive'
  } else if (interpretation.includes('tracks market')) {
    icon = <IoStatsChart size={14} />
    className += ' neutral'
  } else {
    icon = <IoWarning size={14} />
    className += ' negative'
  }

  return (
    <span className={className}>
      {icon}
      {interpretation}
    </span>
  )
}

// Format timestamp for display
function formatTimestamp(ts: string): string {
  const date = new Date(ts)
  return date.toLocaleString()
}

// Colors for pie chart segments
const PIE_COLORS = [
  '#34d399', // emerald
  '#60a5fa', // blue
  '#f472b6', // pink
  '#fbbf24', // amber
  '#a78bfa', // purple
  '#fb7185', // rose
  '#2dd4bf', // teal
  '#fb923c', // orange
]

// Simple SVG Pie Chart component
function SimplePieChart({
  data,
  size = 200,
}: {
  data: { name: string; value: number; color: string }[]
  size?: number
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0)
  if (total === 0) return null

  const radius = size / 2
  const centerX = radius
  const centerY = radius
  const innerRadius = radius * 0.6 // Donut style

  let currentAngle = -Math.PI / 2 // Start from top

  const segments = data.map((item, index) => {
    const angle = (item.value / total) * 2 * Math.PI
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    currentAngle = endAngle

    // Calculate arc path
    const x1 = centerX + radius * Math.cos(startAngle)
    const y1 = centerY + radius * Math.sin(startAngle)
    const x2 = centerX + radius * Math.cos(endAngle)
    const y2 = centerY + radius * Math.sin(endAngle)
    const x3 = centerX + innerRadius * Math.cos(endAngle)
    const y3 = centerY + innerRadius * Math.sin(endAngle)
    const x4 = centerX + innerRadius * Math.cos(startAngle)
    const y4 = centerY + innerRadius * Math.sin(startAngle)

    const largeArcFlag = angle > Math.PI ? 1 : 0

    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ')

    return (
      <path
        key={index}
        d={pathData}
        fill={item.color}
        opacity={0.9}
        style={{ transition: 'opacity 0.2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.9')}
      >
        <title>{`${item.name}: ${item.value.toLocaleString()} (${((item.value / total) * 100).toFixed(1)}%)`}</title>
      </path>
    )
  })

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments}
      {/* Center text */}
      <text
        x={centerX}
        y={centerY - 8}
        textAnchor="middle"
        fill="#f1f5f9"
        fontSize="20"
        fontWeight="600"
      >
        {total.toLocaleString()}
      </text>
      <text
        x={centerX}
        y={centerY + 12}
        textAnchor="middle"
        fill="#94a3b8"
        fontSize="11"
      >
        total events
      </text>
    </svg>
  )
}

type PeriodDays = 7 | 14 | 30 | 90

// Feature Analytics Tab - full analytics page with pie chart and tables
function FeatureAnalyticsTab() {
  const [periodDays, setPeriodDays] = useState<PeriodDays>(7)
  const { features, isLoading: isLoadingFeatures } = useFeatureUsage(periodDays)
  const { trendData, isLoading: isLoadingTrend } = useActivityTrend(periodDays)
  const { onlineCount, activeToday } = useOnlineUsers()

  // Calculate totals
  const totalEvents = features.reduce((sum, f) => sum + f.total_events, 0)
  const maxEvents = features.length > 0 ? Math.max(...features.map(f => f.total_events)) : 1

  return (
    <div className="feature-analytics-tab">
      {/* Period Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>Period:</span>
        {([7, 14, 30, 90] as PeriodDays[]).map((days) => (
          <button
            key={days}
            onClick={() => setPeriodDays(days)}
            style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              background: periodDays === days ? 'var(--accent)' : 'var(--bg-secondary)',
              color: periodDays === days ? 'white' : 'var(--fg-muted)',
            }}
          >
            {days} days
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <CompactCardGrid columns={4}>
        <CompactCard
          label="Online Now"
          value={onlineCount}
          status="healthy"
          icon={<IoRadio size={20} className="pulse-animation" />}
        />
        <CompactCard
          label="Active Today"
          value={activeToday}
          status="neutral"
          icon={<IoPulse size={20} />}
        />
        <CompactCard
          label="Total Events"
          value={isLoadingFeatures ? '...' : totalEvents.toLocaleString()}
          status="neutral"
          icon={<IoStatsChart size={20} />}
        />
        <CompactCard
          label="Features Tracked"
          value={isLoadingFeatures ? '...' : features.length}
          status="neutral"
          icon={<IoPieChart size={20} />}
        />
      </CompactCardGrid>

      {/* Feature Distribution - Pie Chart and Legend */}
      {!isLoadingFeatures && features.length > 0 && (
        <AccordionSection title="Feature Distribution" defaultOpen={true}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {/* Pie Chart */}
            <div style={{ flexShrink: 0 }}>
              <SimplePieChart
                data={features.map((f, i) => ({
                  name: f.feature,
                  value: f.total_events,
                  color: PIE_COLORS[i % PIE_COLORS.length],
                }))}
                size={200}
              />
            </div>
            {/* Legend */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem 1.5rem', flex: 1 }}>
              {features.map((feature, index) => (
                <div key={feature.feature} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', color: 'var(--fg)' }}>
                    {feature.feature}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginLeft: 'auto' }}>
                    {((feature.total_events / totalEvents) * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Feature Breakdown Table */}
      <AccordionSection title="Feature Breakdown" badge={features.length} defaultOpen={true}>
        {isLoadingFeatures ? (
          <div className="admin-empty">Loading feature data...</div>
        ) : features.length === 0 ? (
          <div className="admin-empty">No feature usage data for this period.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {features.map((feature, index) => (
              <div key={feature.feature} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        background: index === 0 ? 'rgba(52, 211, 153, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                        color: index === 0 ? '#34d399' : '#60a5fa',
                      }}
                    >
                      #{index + 1}
                    </span>
                    <span style={{ fontWeight: 500, color: 'var(--fg)' }}>{feature.feature}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--fg-muted)' }}>
                      <IoPeople size={16} />
                      <span>{feature.unique_users}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--fg-muted)' }}>
                      <IoStatsChart size={16} />
                      <span>{feature.total_events.toLocaleString()}</span>
                    </div>
                    <span
                      style={{
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#60a5fa',
                      }}
                    >
                      {feature.avg_events_per_user.toFixed(1)} avg
                    </span>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ height: '8px', borderRadius: '4px', background: 'var(--bg-secondary)', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      borderRadius: '4px',
                      transition: 'width 0.5s',
                      width: `${(feature.total_events / maxEvents) * 100}%`,
                      background: index === 0
                        ? 'linear-gradient(90deg, #34d399, #10b981)'
                        : 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      {/* Daily Activity Trend */}
      <AccordionSection title="Daily Activity Trend" badge={trendData.length} defaultOpen={false}>
        {isLoadingTrend ? (
          <div className="admin-empty">Loading trend data...</div>
        ) : trendData.length === 0 ? (
          <div className="admin-empty">No activity trend data for this period.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 500, color: 'var(--fg-muted)' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 500, color: 'var(--fg-muted)' }}>Unique Users</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 500, color: 'var(--fg-muted)' }}>Total Events</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 500, color: 'var(--fg-muted)' }}>New Users</th>
                </tr>
              </thead>
              <tbody>
                {trendData.slice(-10).reverse().map((day) => (
                  <tr key={day.date} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '0.5rem', color: 'var(--fg)' }}>
                      {new Date(day.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 500, color: '#60a5fa' }}>
                      {day.unique_users}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--fg-muted)' }}>
                      {day.total_events.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {day.new_users > 0 ? (
                        <span style={{ color: '#34d399' }}>+{day.new_users}</span>
                      ) : (
                        <span style={{ color: 'var(--fg-muted)' }}>0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionSection>
    </div>
  )
}

// User Activity Section - shows online users and feature usage
function UserActivitySection() {
  const { onlineCount, activeToday, isLoading: isLoadingOnline } = useOnlineUsers()
  const { features, isLoading: isLoadingFeatures } = useFeatureUsage(7)

  // Get top 3 features by usage
  const topFeatures = features
    .sort((a, b) => b.total_events - a.total_events)
    .slice(0, 3)

  return (
    <div className="user-activity-section" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--fg)' }}>
        User Activity
      </h3>
      <CompactCardGrid columns={4}>
        <CompactCard
          label="Online Now"
          value={isLoadingOnline ? '...' : onlineCount}
          status="healthy"
          icon={<IoRadio size={20} className="pulse-animation" />}
        />
        <CompactCard
          label="Active Today"
          value={isLoadingOnline ? '...' : activeToday}
          status="neutral"
          icon={<IoPulse size={20} />}
        />
        <CompactCard
          label="Top Feature (7d)"
          value={isLoadingFeatures ? '...' : (topFeatures[0]?.feature ?? '—')}
          status="neutral"
          icon={<IoAnalytics size={20} />}
        />
        <CompactCard
          label="Feature Users (7d)"
          value={isLoadingFeatures ? '...' : features.reduce((sum, f) => sum + f.unique_users, 0)}
          status="neutral"
          icon={<IoPeople size={20} />}
        />
      </CompactCardGrid>
    </div>
  )
}

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [stats, setStats] = useState<CLVStats | null>(null)
  const [predictions, setPredictions] = useState<CLVPredictionsResponse | null>(null)
  const [auditLogs, setAuditLogs] = useState<AuditLogsResponse | null>(null)
  const [dbStats, setDbStats] = useState<DBStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [filter, setFilter] = useState<'all' | 'positive' | 'negative'>('all')

  const LIMIT = 25

  const fetchOverviewData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch all admin data in parallel
      const [statsData, predsData, auditData, dbData] = await Promise.all([
        apiGetAuth<CLVStats>('/admin/clv-report').catch(() => null),
        apiGetAuth<CLVPredictionsResponse>(
          `/admin/clv-predictions?limit=${LIMIT}&offset=${page * LIMIT}${
            filter === 'positive' ? '&min_clv=0' : filter === 'negative' ? '&max_clv=0' : ''
          }`
        ).catch(() => null),
        apiGetAuth<AuditLogsResponse>('/admin/audit-logs?limit=10').catch(() => null),
        apiGetAuth<DBStats>('/admin/db-stats').catch(() => null),
      ])

      setStats(statsData)
      setPredictions(predsData)
      setAuditLogs(auditData)
      setDbStats(dbData)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [page, filter])

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverviewData()
    }
  }, [activeTab, fetchOverviewData])

  // Reset page when filter changes
  useEffect(() => {
    setPage(0)
  }, [filter])

  const totalPages = predictions ? Math.ceil(predictions.total / LIMIT) : 0

  // Tab navigation
  const tabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <IoStatsChart size={18} /> },
    { id: 'users', label: 'Users', icon: <IoPeople size={18} /> },
    { id: 'support', label: 'Support', icon: <IoShieldCheckmark size={18} /> },
    { id: 'ops', label: 'Ops', icon: <IoCog size={18} /> },
    { id: 'growth', label: 'Growth', icon: <IoTrendingUp size={18} /> },
    { id: 'analytics', label: 'Analytics', icon: <IoAnalytics size={18} /> },
    { id: 'database', label: 'Database', icon: <IoServer size={18} /> },
  ]

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Console</h1>
          {activeTab === 'overview' && (
            <button
              className="admin-refresh-btn"
              onClick={fetchOverviewData}
              disabled={loading}
              title="Refresh all data"
            >
              <IoRefresh size={18} className={loading ? 'spinning' : ''} />
              <span>Refresh</span>
            </button>
          )}
        </div>
        <p className="admin-subtitle">Phase 11.5 - Support, Ops & Growth</p>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="admin-tab-content">
        {activeTab === 'overview' && (
          <>
            {error && (
              <div className="admin-error">
                <p>Error loading admin data: {error}</p>
                <button onClick={fetchOverviewData}>Retry</button>
              </div>
            )}

            {/* User Activity Section */}
            <UserActivitySection />

            {/* Overview Cards */}
            <CompactCardGrid columns={4}>
              <CompactCard
                label="Total Predictions"
                value={stats?.total_predictions ?? '—'}
                status="neutral"
                icon={<IoStatsChart size={20} />}
              />
              <CompactCard
                label="Average CLV"
                value={stats ? formatCLV(stats.avg_clv) : '—'}
                status={
                  stats?.avg_clv && stats.avg_clv >= 0.02
                    ? 'healthy'
                    : stats?.avg_clv && stats.avg_clv >= 0
                      ? 'neutral'
                      : 'warning'
                }
                icon={<IoTrendingUp size={20} />}
              />
              <CompactCard
                label="Audit Logs"
                value={auditLogs?.total ?? '—'}
                status="neutral"
                icon={<IoDocument size={20} />}
              />
              <CompactCard
                label="Database Size"
                value={dbStats?.total_size ?? '—'}
                status="neutral"
                icon={<IoServer size={20} />}
              />
            </CompactCardGrid>

            {/* Audit Log Section */}
            <AccordionSection
              title="Recent Activity"
              badge={auditLogs?.total}
              defaultOpen={true}
            >
              {auditLogs && auditLogs.logs.length > 0 ? (
                <div className="admin-audit-table">
                  <div className="admin-table-header">
                    <span>Time</span>
                    <span>Admin</span>
                    <span>Action</span>
                    <span>Resource</span>
                    <span>Status</span>
                  </div>
                  {auditLogs.logs.map((log) => (
                    <div key={log.id} className="admin-table-row">
                      <span className="admin-cell-time">{formatTimestamp(log.timestamp)}</span>
                      <span className="admin-cell-admin">{log.admin_email ?? 'Unknown'}</span>
                      <span className="admin-cell-action">
                        <span className={`action-badge ${log.action_type}`}>{log.action_type}</span>
                      </span>
                      <span className="admin-cell-resource">
                        {log.resource_type}
                        {log.resource_id && <span className="resource-id">:{log.resource_id}</span>}
                      </span>
                      <span className={`admin-cell-status ${log.status}`}>{log.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-empty">No audit logs yet. Actions will appear here.</div>
              )}
            </AccordionSection>

            {/* CLV Tracking Section */}
            <AccordionSection
              title="CLV Tracking"
              badge={stats?.total_predictions}
              defaultOpen={false}
            >
              {loading && !stats && <SkeletonCLVStats />}

              {stats && (
                <>
                  {/* Summary Stats */}
                  <div className="clv-summary">
                    <div className="clv-stat-card primary">
                      <div className="clv-stat-label">Average CLV</div>
                      <div className={`clv-stat-value ${getCLVClass(stats.avg_clv)}`}>
                        {formatCLV(stats.avg_clv)}
                      </div>
                      {getInterpretationBadge(stats.interpretation)}
                    </div>

                    <div className="clv-stat-card">
                      <div className="clv-stat-label">Median CLV</div>
                      <div className={`clv-stat-value ${getCLVClass(stats.median_clv)}`}>
                        {formatCLV(stats.median_clv)}
                      </div>
                    </div>

                    <div className="clv-stat-card">
                      <div className="clv-stat-label">Positive CLV %</div>
                      <div className="clv-stat-value">
                        {stats.positive_clv_pct !== null
                          ? `${(stats.positive_clv_pct * 100).toFixed(1)}%`
                          : '—'}
                      </div>
                    </div>

                    <div className="clv-stat-card">
                      <div className="clv-stat-label">Total Predictions</div>
                      <div className="clv-stat-value">{stats.total_predictions}</div>
                    </div>
                  </div>

                  {/* By Market Breakdown */}
                  {stats.by_market && Object.keys(stats.by_market).length > 0 && (
                    <div className="clv-section">
                      <h3>CLV by Market</h3>
                      <div className="clv-breakdown-grid">
                        {Object.entries(stats.by_market).map(([market, data]) => (
                          <div key={market} className="clv-breakdown-item">
                            <span className="clv-breakdown-label">{market}</span>
                            <span className={`clv-breakdown-value ${getCLVClass(data.avg_clv)}`}>
                              {formatCLV(data.avg_clv)}
                            </span>
                            <span className="clv-breakdown-count">({data.count})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Predictions List */}
                  <div className="clv-section">
                    <div className="clv-section-header">
                      <h3>Recent Predictions</h3>
                      <div className="clv-filter-tabs">
                        <button
                          className={`clv-filter-tab ${filter === 'all' ? 'active' : ''}`}
                          onClick={() => setFilter('all')}
                        >
                          All
                        </button>
                        <button
                          className={`clv-filter-tab ${filter === 'positive' ? 'active' : ''}`}
                          onClick={() => setFilter('positive')}
                        >
                          <IoTrendingUp size={14} /> Positive
                        </button>
                        <button
                          className={`clv-filter-tab ${filter === 'negative' ? 'active' : ''}`}
                          onClick={() => setFilter('negative')}
                        >
                          <IoTrendingDown size={14} /> Negative
                        </button>
                      </div>
                    </div>

                    {predictions && predictions.predictions.length > 0 ? (
                      <>
                        <div className="clv-predictions-table">
                          <div className="clv-table-header">
                            <span>Market</span>
                            <span>Outcome</span>
                            <span>Our Prob</span>
                            <span>Pinnacle</span>
                            <span>CLV</span>
                            <span>Result</span>
                          </div>
                          {predictions.predictions.map((pred) => (
                            <div key={pred.id} className="clv-table-row">
                              <span className="clv-market">
                                {pred.market_type}
                                {pred.line !== null && ` ${pred.line}`}
                              </span>
                              <span className="clv-outcome">{pred.outcome}</span>
                              <span className="clv-prob">
                                {(pred.predicted_prob * 100).toFixed(1)}%
                              </span>
                              <span className="clv-pinnacle">
                                {pred.pinnacle_odds !== null
                                  ? `${pred.pinnacle_odds.toFixed(2)} (${(pred.pinnacle_implied_prob! * 100).toFixed(1)}%)`
                                  : '—'}
                              </span>
                              <span className={`clv-value ${getCLVClass(pred.clv)}`}>
                                {formatCLV(pred.clv)}
                              </span>
                              <span className="clv-result">
                                {pred.actual_outcome === null ? (
                                  <span className="pending">Pending</span>
                                ) : pred.actual_outcome ? (
                                  <span className="won">Won</span>
                                ) : (
                                  <span className="lost">Lost</span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="pagination">
                            <button
                              disabled={page === 0}
                              onClick={() => setPage((p) => Math.max(0, p - 1))}
                            >
                              Previous
                            </button>
                            <span>
                              Page {page + 1} of {totalPages}
                            </span>
                            <button
                              disabled={page >= totalPages - 1}
                              onClick={() => setPage((p) => p + 1)}
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="admin-empty">
                        <p>No CLV predictions yet.</p>
                        <p>CLV data will appear after fixtures kick off and closing odds are captured.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </AccordionSection>

            {/* Database Stats Section */}
            <AccordionSection title="Database Stats" defaultOpen={false}>
              {dbStats ? (
                <div className="admin-db-stats">
                  <div className="db-stats-summary">
                    <p>
                      <strong>Total Size:</strong> {dbStats.total_size}
                    </p>
                    {dbStats.oldest_odds_history && (
                      <p>
                        <strong>Odds History Range:</strong> {dbStats.oldest_odds_history} to{' '}
                        {dbStats.newest_odds_history}
                      </p>
                    )}
                  </div>
                  <div className="db-stats-grid">
                    {Object.entries(dbStats.tables || {})
                      .sort(([, a], [, b]) => b.row_count - a.row_count)
                      .slice(0, 10)
                      .map(([table, info]) => (
                        <div key={table} className="db-stats-item">
                          <span className="db-table-name">{table}</span>
                          <span className="db-row-count">{info.row_count.toLocaleString()} rows</span>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="admin-empty">Loading database stats...</div>
              )}
            </AccordionSection>

            {/* Info Section */}
            <AccordionSection title="CLV Guide" defaultOpen={false}>
              <div className="clv-info">
                <h4>Understanding CLV</h4>
                <p>
                  <strong>CLV (Closing Line Value)</strong> = Our predicted probability - Pinnacle's
                  implied probability
                </p>
                <p>
                  Pinnacle is considered the sharpest bookmaker. Their closing lines represent the
                  market's best estimate of true probability. Positive CLV over 1000+ predictions
                  indicates genuine model edge.
                </p>
                <div className="clv-legend">
                  <span className="clv-legend-item clv-strong-positive">&gt; +2%: Strong edge</span>
                  <span className="clv-legend-item clv-positive">0% to +2%: Slight edge</span>
                  <span className="clv-legend-item clv-neutral">-2% to 0%: Tracks market</span>
                  <span className="clv-legend-item clv-negative">&lt; -2%: Underperforms</span>
                </div>
              </div>
            </AccordionSection>
          </>
        )}

        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'support' && <SupportConsole />}
        {activeTab === 'ops' && <OpsConsole />}
        {activeTab === 'growth' && <GrowthConsole />}
        {activeTab === 'analytics' && <FeatureAnalyticsTab />}
        {activeTab === 'database' && <DatabasePage />}
      </div>
    </div>
  )
}
